import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import progressService, { UserProgress } from '../services/progressService';
import { buildProgressKey, progressQueryKey } from './progressCache';

interface UseBatchProgressReturn {
    progressMap: Map<string, UserProgress>;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    getProgress: (docId: string, collectionPath?: string) => UserProgress | null;
}

const EMPTY_PROGRESS = new Map<string, UserProgress>();

// Every card on a page calls this hook; they all share one cached query per user.
export const useBatchProgress = (): UseBatchProgressReturn => {
    const { user } = useAuth();
    const userId = user?.uid;

    const { data, isFetching, error, refetch } = useQuery({
        queryKey: progressQueryKey(userId || ''),
        queryFn: () => progressService.batchFetchAllUserProgress(userId!),
        enabled: !!userId,
    });

    const progressMap = (userId && data) || EMPTY_PROGRESS;

    const getProgress = useCallback((docId: string, collectionPath?: string): UserProgress | null => {
        if (!userId) return null;
        return progressMap.get(buildProgressKey(docId, collectionPath)) || null;
    }, [progressMap, userId]);

    const refetchProgress = useCallback(async () => {
        if (!userId) return;
        await refetch();
    }, [refetch, userId]);

    return {
        progressMap,
        loading: isFetching,
        error: error ? (error as Error).message || 'Failed to fetch progress data' : null,
        refetch: refetchProgress,
        getProgress,
    };
};
